// frame: ondv5 @ ui/dogdisk-user.pen ShareActionSheet 分享菜单(2026-06-11 对齐完整版)
// 触发:share-list 卡片 more 按钮
//
// variant='owner'(默认):复制链接 / 资源包设置(→/pack/manage/:id)/ 显示二维码(→uJtA2)/
// 取消分享(→mdYih 由触发方确认)或 恢复分享
//(2026-06-13 管理页合一:原「详细信息」→M6qlR /share-manage/:id 已删,信息均在 /pack/manage/:id)
// variant='subscriber':仅复制链接(订阅 tab 无 owner 动作)
import { useNavigate } from 'react-router-dom'
import { copyToClipboard } from '@dogdisk/shared'
import { CircleX, Folder, Link as LinkIcon, QrCode, RotateCcw, Settings } from 'lucide-react'

export type ShareActionSheetProps = {
  open: boolean
  onClose: () => void
  shareId: string
  shareLink: string
  packName: string
  /** 'owner' 全量动作;'subscriber' 仅复制链接 */
  variant?: 'owner' | 'subscriber'
  /** FileInfo 头副标题,如「12次访问」 */
  subtitle?: string
  /** 'active' 显示取消分享;其余显示恢复分享(subscriber variant 用不到) */
  status?: 'active' | 'cancelled' | 'expired'
  /** 点 "取消分享"(由触发方弹 mdYih 确认) */
  onCancelShare?: () => void
  /** 点 "恢复分享" */
  onRestoreShare?: () => void
  /** 点 "显示二维码"(由触发方挂 uJtA2 modal) */
  onShowQr?: () => void
}

export function ShareActionSheet({
  open,
  onClose,
  shareId,
  shareLink,
  packName,
  variant = 'owner',
  subtitle,
  status,
  onCancelShare,
  onRestoreShare,
  onShowQr,
}: ShareActionSheetProps) {
  const navigate = useNavigate()
  if (!open) return null

  const isOwner = variant === 'owner'

  const onCopy = async () => {
    await copyToClipboard(shareLink)
    onClose()
  }

  const row = 'flex h-14 w-full items-center gap-4 px-6 text-left active:bg-[#F6F7F8]'

  return (
    <div
      data-frame-id="ondv5"
      data-testid="ondv5"
      role="dialog"
      aria-modal="true"
      aria-label="分享菜单"
      className="fixed inset-0 z-50 flex items-end bg-black/50"
      onClick={onClose}
    >
      <div
        className="mx-auto w-full max-w-[402px] rounded-t-3xl bg-white"
        style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-2 pb-3">
          <span className="h-1 w-10 rounded-full bg-[#E0E0E0]" />
        </div>

        {/* FileInfo(occAA) */}
        <div className="flex items-center gap-3.5 px-6 py-3">
          <Folder size={44} className="shrink-0 text-[#60A5FA]" strokeWidth={1.5} aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[17px] font-semibold text-[#1A1A1A]">{packName}</p>
            {subtitle && <p className="text-[14px] text-[#9CA3AF]">{subtitle}</p>}
          </div>
        </div>
        <div className="h-2 bg-[#F6F7F8]" />

        <ul className="flex flex-col">
          <li>
            <button type="button" data-testid="ondv5-copy" onClick={() => void onCopy()} className={row}>
              <LinkIcon size={22} className="text-[#1A1A1A]" aria-hidden="true" />
              <span className="text-[17px] text-[#1A1A1A]">复制链接</span>
            </button>
          </li>
          {isOwner && (
            <>
              <li>
                <button
                  type="button"
                  data-testid="ondv5-settings"
                  onClick={() => {
                    onClose()
                    navigate(`/pack/manage/${shareId}`)
                  }}
                  className={row}
                >
                  <Settings size={22} className="text-[#1A1A1A]" aria-hidden="true" />
                  <span className="text-[17px] text-[#1A1A1A]">资源包设置</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  data-testid="ondv5-qr"
                  onClick={() => {
                    onClose()
                    onShowQr?.()
                  }}
                  className={row}
                >
                  <QrCode size={22} className="text-[#1A1A1A]" aria-hidden="true" />
                  <span className="text-[17px] text-[#1A1A1A]">显示二维码</span>
                </button>
              </li>
            </>
          )}
        </ul>
        {isOwner && (
          <>
            <div className="h-2 bg-[#F6F7F8]" />
            {status === 'active' ? (
              <button
                type="button"
                data-testid="ondv5-cancel-share"
                onClick={() => {
                  onClose()
                  onCancelShare?.()
                }}
                className={row}
              >
                <CircleX size={22} className="text-[#FF6B6B]" aria-hidden="true" />
                <span className="text-[17px] text-[#FF6B6B]">取消分享</span>
              </button>
            ) : (
              <button
                type="button"
                data-testid="ondv5-restore-share"
                onClick={() => {
                  onClose()
                  onRestoreShare?.()
                }}
                className={row}
              >
                <RotateCcw size={22} className="text-[#19caad]" aria-hidden="true" />
                <span className="text-[17px] text-[#19caad]">恢复分享</span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
